import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { snakeCase } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import type { LocaleListProps, LocaleListRef } from './LocaleList.data';
import styles from './LocaleList.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './LocaleList.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import localeConfig from '../../../../locale.config';
import { observer } from 'mobx-react-lite';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import { useRouter } from 'next/router';
import { useGlobalStore } from '../../../../stores/global';
import { renderCta } from '../../../../util/cta/cta.template';
import Icon from '../../../atoms/icon/Icon';
import { stripLocaleFromUrl } from '../../../../util/url.utils';
import MenuItem from '../../../atoms/menu-item/MenuItem';
import { useMobileViewport } from '../../../../hooks/useMobileViewport';
import sharedVariables from '../../../../data/shared-variable/shared-variables.json';
import Text from '../../../atoms/text/Text';

function LocaleList({
  className,
  ref,
}: LocaleListProps & { ref?: Ref<LocaleListRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const listRef = useRef<HTMLUListElement>(null);
  const router = useRouter();
  const [currentLocale, setCurrentLocale] = useState(router.locale);
  const globalStore = useGlobalStore();
  const { localeListIsOpen } = globalStore;
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_1024);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        listRef,
        isMobileViewport,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, {
    onMount: true,
    value: localeListIsOpen,
  });

  const onCloseClick = useCallback(() => {
    globalStore.setLocaleListOpenState(false);
  }, [globalStore]);

  useUpdateEffect(() => {
    document.cookie = `NEXT_LOCALE=${currentLocale}; max-age=31536000;`;
    window.location.href = `${window.location.origin}/${`${currentLocale}/${stripLocaleFromUrl(
      router.asPath,
      currentLocale,
    )}`.replace('//', '/')}`;

    // change just the locale and maintain all other route information including href's query
    // router.push({ pathname, query }, asPath, { locale: selectedLocal })
  }, [currentLocale]);

  return (
    <div ref={elementRef} className={classNames(styles.localeList, className)}>
      <ul ref={listRef} className={classNames(styles.list)}>
        <li>
          <Text
            as="p"
            size="label-large"
            transitionInType="none"
            disableTriggerInOnScroll={false}
            className={styles.topText}
          >
            {globalStore.globalSettings.label_language_selection}
          </Text>
        </li>
        {localeConfig.locales?.map((locale) => (
          <li key={locale} className={styles.listItem}>
            <MenuItem disabled={locale === currentLocale} onClick={() => setCurrentLocale(locale)}>
              {/* @ts-ignore */}
              {globalStore.globalSettings[`language_${snakeCase(locale)}`] || locale}
            </MenuItem>
          </li>
        ))}
      </ul>

      {renderCta({
        innerTemplate: <Icon name="close" className={classNames(styles.icon)} />,
        props: {
          className: classNames(styles.closeButton),
          onClick: onCloseClick,
          'aria-label': 'Click to close',
        },
      })}

      {renderCta({
        innerTemplate: <Icon name="chevron-left" className={classNames(styles.icon)} />,
        props: {
          className: classNames(styles.backButton),
          onClick: onCloseClick,
          'aria-label': 'Go back',
        },
      })}
    </div>
  );
}

export default observer(LocaleList);
